import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import authAxios from '../utils/authAxios';
import { Button, Input } from '../styles/TableStyles';
import { PageHeader } from '../styles/CustomerSpecsIndexStyles';
import SecureImage from '../components/SecureImage';

const FOLDER = 'CustomerSpecs/TiHi';
const initialForm = { TiHi: '', ImageLink: '' };

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
`;

const Card = styled.div`
  background: var(--white-a25);
  border: 1px solid var(--shadow-a08);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const ThumbWrap = styled.div`
  height: 160px; /* image ~3/4 of typical card */
  background: var(--shadow-a05);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Title = styled.div`
  text-align: center;
  font-weight: 700;
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  width: 100%;

  /* Ensure the inner row spans the same width/padding as other sections */
  & > .action-row {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }

  /* Make the three buttons visually consistent and aligned */
  & button {
    flex: 1 1 0;
  }
`;


const ActionButton = styled(Button)`
  background-color: ${({ $bg }) => $bg || 'var(--green-800)'};
  &:hover {
    background-color: ${({ $hover }) => $hover || 'var(--green-900)'};
  }
`;
const TiHiPage = () => {
  const [rows, setRows] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: 'base', numeric: true }), []);

  const fetchRows = async () => {
    try {
      const res = await authAxios.get('/api/tihi');
      setRows(res.data || []);
    } catch (e) { console.error('Fetch TiHi failed', e); }
  };
  useEffect(() => { fetchRows(); }, []);

  const sorted = useMemo(() => (
    [...rows].sort((a, b) => collator.compare(a.TiHi ?? '', b.TiHi ?? ''))
  ), [rows, collator]);

  const startEdit = (row) => {
    setEditingId(row.UniqueID);
    setFormData({ TiHi: row.TiHi ?? '', ImageLink: row.ImageLink ?? '' });
  };
  const cancelEdit = () => { setEditingId(null); setShowAdd(false); setFormData(initialForm); };

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('image', file);
      const base = String(formData.TiHi || file.name || 'tihi').trim();
      fd.append('name', base);
      const res = await authAxios.post(`/uploadImage/${FOLDER}`, fd);
      const blobName = res?.data?.blobName || '';
      setFormData(prev => ({ ...prev, ImageLink: blobName }));
    } catch (e) {
      console.error('[TiHi] upload failed', e);
      alert('Image upload failed.');
    }
  };

  const save = async () => {
    if (!formData.TiHi?.trim()) return alert('Ti x Hi is required');
    try {
      setSubmitting(true);
      if (editingId) {
        await authAxios.put(`/api/tihi/${editingId}`, formData);
      } else {
        await authAxios.post('/api/tihi', formData);
      }
      cancelEdit();
      fetchRows();
    } catch (e) {
      console.error('Save failed', e);
      alert('Save failed.');
    } finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this Ti x Hi?')) return;
    try {
      await authAxios.delete(`/api/tihi/${id}`);
      if (editingId === id) cancelEdit();
      fetchRows();
    } catch (e) {
      console.error('Delete failed', e);
      alert('Delete failed.');
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <PageHeader>
        <div>Ti x Hi</div>
        <div style={{ display:'flex', gap:8 }}>
          {!showAdd && editingId == null && (
            <Button onClick={() => { setShowAdd(true); setFormData(initialForm); }}>Add Entry</Button>
          )}
        </div>
      </PageHeader>

      <Grid>
        {showAdd && (
          <Card>
            <ThumbWrap>
              {formData.ImageLink ? (
                <SecureImage
                  src={String(formData.ImageLink).replace(/^CustomerSpecs\/TiHi\//,'')}
                  folder={FOLDER}
                  alt={formData.TiHi || ''}
                  style={{ width:'100%', height:'100%', objectFit:'contain' }}
                />
              ) : (
                <div style={{ color:'var(--grey-500)' }}>No Image</div>
              )}
            </ThumbWrap>
            <Title>
              <Input name="TiHi" value={formData.TiHi} onChange={(e)=> setFormData(p=>({ ...p, TiHi: e.target.value }))} placeholder="Ti x Hi (e.g., 10x12)" />
            </Title>
            <div style={{ marginTop:8 }}>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e)=> handleUpload(e.target.files?.[0] || null)} />
            </div>
            <Actions>
              <div className="action-row">
                <ActionButton $bg="var(--grey-500)" $hover="var(--grey-600)" onClick={cancelEdit}>Cancel</ActionButton>
                <ActionButton $bg="var(--green-600)" $hover="var(--green-700)" onClick={save} disabled={submitting}>Save Changes</ActionButton>
              </div>
            </Actions>
          </Card>
        )}

        {sorted.map(row => {
          const filename = String(row.ImageLink || '').replace(/^CustomerSpecs\/TiHi\//, '');
          const isEditingRow = editingId === row.UniqueID;
          return (
            <Card key={row.UniqueID}>
              <ThumbWrap onClick={() => { if (!isEditingRow) startEdit(row); }} style={{ cursor: isEditingRow ? 'default' : 'pointer' }}>
                {isEditingRow ? (
                  formData.ImageLink ? (
                    <SecureImage
                      src={String(formData.ImageLink).replace(/^CustomerSpecs\/TiHi\//,'')}
                      folder={FOLDER}
                      alt={formData.TiHi || ''}
                      style={{ width:'100%', height:'100%', objectFit:'contain' }}
                    />
                  ) : (
                    <div style={{ color:'var(--grey-500)' }}>No Image</div>
                  )
                ) : (
                  filename ? (
                    <SecureImage
                      src={filename}
                      folder={FOLDER}
                      alt={row.TiHi || filename}
                      style={{ width:'100%', height:'100%', objectFit:'contain' }}
                    />
                  ) : (
                    <div style={{ color:'var(--grey-500)' }}>No Image</div>
                  )
                )}
              </ThumbWrap>

              {isEditingRow ? (
                <>
                  <Title>
                    <Input name="TiHi" value={formData.TiHi} onChange={(e)=> setFormData(p=>({ ...p, TiHi: e.target.value }))} />
                  </Title>
                  <div style={{ marginTop:8 }}>
                    <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e)=> handleUpload(e.target.files?.[0] || null)} />
                  </div>
                  <Actions>
                    <div className="action-row">
                      <ActionButton $bg="var(--red-600)" $hover="var(--color-danger)" onClick={() => remove(row.UniqueID)}>Delete</ActionButton>
                      <ActionButton $bg="var(--grey-500)" $hover="var(--grey-600)" onClick={cancelEdit}>Cancel</ActionButton>
                      <ActionButton $bg="var(--green-600)" $hover="var(--green-700)" onClick={save} disabled={submitting}>Save Changes</ActionButton>
                    </div>
                  </Actions>
                </>
              ) : (
                <Title>{row.TiHi}</Title>
              )}
            </Card>
          );
        })}
      </Grid>
    </div>
  );
};

export default TiHiPage;


